export type EvidenceStrength = 'STRONG' | 'MODERATE' | 'LIMITED' | 'CONFLICTING' | 'INSUFFICIENT' | string;

export interface MachineMemoryInsightDto {
  insight_id: string;
  title?: string | null;
  statement?: string | null;
  type?: string | null;
  certainty?: string | null;
  evidence_strength?: EvidenceStrength | null;
  why_it_matters?: string | null;
  limitations?: string[];
  display_priority?: number | string | null;
  supporting_document_ids?: string[];
  supporting_fact_ids?: string[];
  supporting_knowledge_chunk_ids?: string[];
  supporting_relationship_ids?: string[];
  [key: string]: unknown;
}

export interface MachineMemoryRelationshipEvidenceDto {
  document_ids?: string[];
  document_summary_ids?: string[];
  fact_ids?: string[];
  knowledge_chunk_ids?: string[];
}

export interface MachineMemoryRelationshipDto {
  relationship_id: string;
  title?: string | null;
  statement?: string | null;
  type?: string | null;
  evidence_strength?: EvidenceStrength | null;
  related_components?: string[];
  uncertainty_notes?: string | null;
  supporting_evidence?: MachineMemoryRelationshipEvidenceDto;
  conflicting_evidence?: MachineMemoryRelationshipEvidenceDto;
  [key: string]: unknown;
}

export interface MachineMemoryGapDto {
  gap_id: string;
  gap_type?: string | null;
  statement?: string | null;
  limitations?: string[];
  supporting_document_ids?: string[];
  supporting_relationship_ids?: string[];
  [key: string]: unknown;
}

export interface MachineMemoryConflictDto {
  conflict_id: string;
  conflict_type?: string | null;
  statement?: string | null;
  field?: string | null;
  machine_value?: unknown;
  evidence_values?: unknown[];
  document_ids?: string[];
  fact_ids?: string[];
  knowledge_chunk_ids?: string[];
  [key: string]: unknown;
}

export interface MachineMemoryThemeDto {
  theme_id?: string | null;
  title?: string | null;
  statement?: string | null;
  evidence_strength?: EvidenceStrength | null;
  supporting_document_ids?: string[];
  [key: string]: unknown;
}

export interface MachineMemoryKnowledgeEvidenceDto {
  artifact_type: string;
  artifact_id: string;
  document_id?: string | null;
  source_locator?: Record<string, unknown> | null;
}

export interface MachineMemoryInvestigationKnowledgeDto {
  record_id: string;
  knowledge_id?: string | null;
  knowledge_type?: string | null;
  statement?: string | null;
  investigation_id: string;
  closure_timestamp?: string | null;
  reasoning_state_version?: number | string | null;
  authority?: string | null;
  supporting_evidence?: MachineMemoryKnowledgeEvidenceDto[];
  [key: string]: unknown;
}

export interface MachineWorkspaceDocumentDto {
  document_id: string;
  filename?: string | null;
  file_extension?: string | null;
  processing_status?: string | null;
  lifecycle_status?: string | null;
  created_at?: string | null;
  uploaded_at?: string | null;
}

export interface MachineWorkspaceInvestigationDto {
  investigation_id: string;
  title?: string | null;
  objective?: string | null;
  status?: string | null;
  closure?: {
    outcome?: string | null;
    what_happened?: string | null;
    what_learned?: string | null;
    what_was_done?: string | null;
    what_happened_afterward?: string | null;
    follow_up?: string | null;
    closed_at?: string | null;
  } | null;
  updated_at?: string | null;
}

export interface MachineWorkspaceResponseDto {
  schema_version: string;
  workspace_status: string;
  machine: {
    machine_id: string;
    machine_name?: string | null;
    machine_type?: string | null;
    asset_id?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    facility_id?: string | null;
    location?: string | null;
    production_area?: string | null;
    production_line?: string | null;
    [key: string]: unknown;
  };
  machine_memory: {
    machine_memory_id?: string | null;
    memory_version?: number | string | null;
    status?: string | null;
    summary?: string | null;
    updated_at?: string | null;
    source_snapshot_id?: string | null;
    size_monitoring?: Record<string, unknown>;
  };
  current_understanding: {
    document_profile?: {
      active_document_count?: number | string | null;
      date_coverage?: { start?: string | null; end?: string | null } | null;
      document_types?: Record<string, number | string>;
      [key: string]: unknown;
    };
    evidence_profile?: {
      active_fact_count?: number | string | null;
      active_knowledge_chunk_count?: number | string | null;
      commonly_referenced_components?: Array<Record<string, unknown>>;
      commonly_referenced_topics?: string[];
      [key: string]: unknown;
    };
    insights?: MachineMemoryInsightDto[];
    relationships?: MachineMemoryRelationshipDto[];
    documentation_gaps?: MachineMemoryGapDto[];
    known_conflicts?: MachineMemoryConflictDto[];
    recurring_themes?: MachineMemoryThemeDto[];
    investigation_knowledge?: MachineMemoryInvestigationKnowledgeDto[];
  };
  documents: MachineWorkspaceDocumentDto[];
  recent_documents: MachineWorkspaceDocumentDto[];
  timeline: Array<Record<string, unknown>>;
  investigations: MachineWorkspaceInvestigationDto[];
  counts: {
    documents?: number | string;
    facts?: number | string;
    knowledge_chunks?: number | string;
    relationships?: number | string;
    insights?: number | string;
    documentation_gaps?: number | string;
    known_conflicts?: number | string;
    machine_knowledge?: number | string;
    investigations?: number | string;
  };
  capabilities?: Record<string, boolean>;
  request_id?: string;
}

export type MachineMemoryItem =
  | { kind: 'INSIGHT'; value: MachineMemoryInsightDto }
  | { kind: 'RELATIONSHIP'; value: MachineMemoryRelationshipDto }
  | { kind: 'GAP'; value: MachineMemoryGapDto }
  | { kind: 'CONFLICT'; value: MachineMemoryConflictDto }
  | { kind: 'THEME'; value: MachineMemoryThemeDto }
  | { kind: 'INVESTIGATION_KNOWLEDGE'; value: MachineMemoryInvestigationKnowledgeDto };

export function asCount(value: number | string | null | undefined): number {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
}

export function humanMemoryType(value: string | null | undefined): string {
  return String(value ?? '')
    .replace(/_/g, ' ')
    .toLocaleLowerCase()
    .replace(/\b\w/g, (letter) => letter.toLocaleUpperCase());
}

export function evidenceStrengthLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const normalized = value.toLocaleUpperCase();
  if (normalized === 'STRONG') return 'Strong';
  if (normalized === 'MODERATE') return 'Moderate';
  if (normalized === 'LIMITED') return 'Limited';
  if (normalized === 'CONFLICTING') return 'Conflicting';
  if (normalized === 'INSUFFICIENT') return 'Insufficient';
  return humanMemoryType(value);
}
